<template>
  <section class="task-panel" data-testid="session-task-panel">
    <div class="task-panel__heading">
      <span class="task-panel__title">{{ $t('bo.programs.creation.tasks') }}</span>
      <span class="task-panel__count">{{ tasks.length }}/{{ MAX_SESSION_TASKS }}</span>
    </div>

    <article v-for="(task, index) in tasks" :key="`${task.task_title}-${index}`" class="task-panel__task">
      <div class="task-panel__task-heading">
        <span class="task-panel__label">{{ $t('bo.programs.creation.taskNumber', { n: index + 1 }) }}</span>
        <button v-if="!readonly" type="button" class="link-btn danger" @click="removeTask(index)">
          {{ $t('bo.common.delete') }}
        </button>
      </div>
      <div class="task-panel__value">{{ task.task_title }}</div>
      <div class="task-panel__value muted">{{ task.task_instructions || '—' }}</div>
      <div class="task-panel__value muted">{{ formatLabel(task.task_input_type) }}</div>
    </article>

    <div v-if="isAdding" class="task-panel__form">
      <div class="form-group">
        <label>{{ $t('bo.programs.creation.taskTitle') }} <span class="required">*</span></label>
        <input v-model="draftTitle" type="text" class="input" maxlength="255" data-testid="session-task-title" />
      </div>
      <div class="form-group">
        <label>{{ $t('bo.programs.creation.taskDescription') }}</label>
        <textarea v-model="draftInstructions" class="textarea" rows="2" maxlength="5000" data-testid="session-task-instructions" />
      </div>
      <div class="form-group">
        <label>{{ $t('bo.programs.creation.taskFormat') }}</label>
        <select v-model="draftInputType" class="input" data-testid="session-task-format">
          <option v-for="option in formatOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </div>
      <p v-if="formError" class="task-panel__error" role="alert">{{ formError }}</p>
      <div class="task-panel__actions">
        <button type="button" class="btn-secondary" @click="cancelAdd">{{ $t('bo.common.cancel') }}</button>
        <button type="button" class="btn-primary" data-testid="session-task-save" @click="commitDraft">
          {{ $t('bo.programs.creation.addTask') }}
        </button>
      </div>
    </div>

    <button
      v-if="!readonly && !atMax && !isAdding"
      type="button"
      class="link-btn"
      data-testid="session-task-add"
      @click="startAdd"
    >
      + {{ $t('bo.programs.creation.addTask') }}
    </button>
    <p v-if="atMax" class="task-panel__hint">{{ $t('bo.programs.creation.tasksMax', { max: MAX_SESSION_TASKS }) }}</p>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { MAX_SESSION_TASKS } from '@/stores/adminProgramCreation'

const props = defineProps({
  tasks: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
})

const emit = defineEmits(['change'])
const { t } = useI18n()

const formatOptions = computed(() => [
  { value: 'text', label: t('bo.programs.creation.taskFormatText') },
  { value: 'pdf', label: t('bo.programs.creation.taskFormatDocument') },
  { value: 'link', label: t('bo.programs.creation.taskFormatLink') },
])

const isAdding = ref(false)
const draftTitle = ref('')
const draftInstructions = ref('')
const draftInputType = ref('text')
const formError = ref('')

const atMax = computed(() => props.tasks.length >= MAX_SESSION_TASKS)

const formatLabel = (value) => formatOptions.value.find((item) => item.value === value)?.label || value

const resetDraft = () => {
  draftTitle.value = ''
  draftInstructions.value = ''
  draftInputType.value = 'text'
  formError.value = ''
}

const startAdd = () => {
  if (props.readonly || atMax.value) return
  resetDraft()
  isAdding.value = true
}

const cancelAdd = () => {
  isAdding.value = false
  resetDraft()
}

/** Adds the task being typed. Called by the session form before saving, so a typed task is never lost. */
const commitDraft = () => {
  if (!isAdding.value) return true
  const title = draftTitle.value.trim()
  const instructions = draftInstructions.value.trim()
  if (!title && !instructions) {
    cancelAdd()
    return true
  }
  if (!title) {
    formError.value = t('bo.programs.creation.taskTitleRequired')
    return false
  }
  if (atMax.value) return false

  emit('change', [
    ...props.tasks,
    { task_title: title, task_instructions: instructions, task_input_type: draftInputType.value },
  ])
  isAdding.value = false
  resetDraft()
  return true
}

const removeTask = (index) => {
  emit('change', props.tasks.filter((_, taskIndex) => taskIndex !== index))
}

defineExpose({ commitDraft })
</script>

<style scoped>
.task-panel {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.9rem;
}

.task-panel__heading,
.task-panel__task-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.task-panel__title {
  font-size: 0.875rem;
  font-weight: 600;
}

.task-panel__count,
.task-panel__hint,
.muted {
  font-size: 0.8rem;
  color: #6b7280;
}

.task-panel__task {
  border-top: 1px solid #f3f4f6;
  padding: 0.5rem 0;
}

.task-panel__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
}

.task-panel__value {
  font-size: 0.85rem;
  margin-top: 0.2rem;
}

.task-panel__form {
  border-top: 1px solid #f3f4f6;
  padding-top: 0.5rem;
}

.task-panel__error {
  color: #dc2626;
  font-size: 0.8rem;
  margin: 0 0 0.5rem;
}

.task-panel__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.6rem;
}

.form-group label {
  font-size: 0.8rem;
  font-weight: 600;
}

.required {
  color: #dc2626;
}

.input,
.textarea {
  padding: 0.45rem 0.7rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
}

.link-btn {
  border: none;
  background: none;
  color: #3730a3;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.25rem 0;
}

.link-btn.danger {
  color: #dc2626;
}

.btn-primary,
.btn-secondary {
  padding: 0.4rem 0.9rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-secondary {
  background: white;
  border: 1px solid #d1d5db;
}
</style>
